import { useState, useEffect } from "react";
import { translations } from "../../i18n";
import { trackLead } from "../../lib/pixel";
import { buildRegisterUrl } from "../../lib/tracking-url";

type Translation = (typeof translations)["de"];

function LiveFeed({
  isVisible,
  t,
}: {
  isVisible: (id: string) => boolean;
  t: Translation["liveFeed"];
}) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const projects = t.projects;

  useEffect(() => {
    if (!isVisible("feed")) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 3 >= projects.length ? 0 : prev + 3));
    }, 8000);

    return () => clearInterval(interval);
  }, [isVisible, projects.length]);

  const visibleProjects = projects.slice(currentIndex, currentIndex + 3);
  if (visibleProjects.length < 3 && currentIndex > 0) {
    visibleProjects.push(...projects.slice(0, 3 - visibleProjects.length));
  }

  return (
    <section
      id="feed"
      data-animate
      className={`py-16 sm:py-24 md:py-32 bg-slate-900/30 transition-all duration-1000 ${
        isVisible("feed")
          ? "opacity-100 translate-y-0"
          : "opacity-0 translate-y-10"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-center mb-3 sm:mb-4 md:mb-6">{t.heading}</h2>
        <p className="text-base sm:text-lg md:text-xl text-slate-400 text-center mb-10 sm:mb-12 md:mb-16">
          {t.subheading}
        </p>

        <div className="grid md:grid-cols-3 gap-4 sm:gap-5 md:gap-6">
          {visibleProjects.map((project, idx) => (
            <div
              key={`${currentIndex}-${idx}`}
              className="p-6 bg-slate-800/50 border border-slate-700/50 rounded-xl hover:border-orange-500/50 transition-all hover:bg-slate-800/70 group cursor-pointer shadow-lg hover:shadow-orange-500/10 animate-fade-in-up"
              style={{ animationDelay: `${idx * 150}ms` }}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div className="px-2 py-1 bg-orange-500/20 text-orange-400 rounded text-xs font-medium">
                    {project.city}
                  </div>
                  <div className="text-xs text-slate-500">{project.date}</div>
                </div>
              </div>

              <h3 className="text-lg font-semibold mb-3 group-hover:text-orange-500 transition-colors leading-snug">
                {project.title}
              </h3>

              <p className="text-slate-400 text-sm mb-4 leading-relaxed">
                {project.summary}
              </p>

              <div className="mb-4 pb-4 border-b border-slate-700/50">
                <div className="text-xs text-slate-500 mb-1">
                  {t.signalSourceLabel}
                </div>
                <div className="text-sm text-slate-300">
                  {project.signalSource}
                </div>
              </div>

              <div className="mb-4">
                <div className="text-xs text-slate-500 mb-1">
                  {t.tenderLabel}
                </div>
                <div className="text-sm text-orange-400 font-medium">
                  {project.tenderTimeline}
                </div>
              </div>

              <div className="flex flex-wrap gap-2 mb-4">
                {project.tags.map((tag, tagIdx) => (
                  <span
                    key={tagIdx}
                    className="px-2 py-1 bg-slate-700/50 text-slate-300 rounded text-xs group-hover:bg-slate-700 transition-colors"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <a
                href={buildRegisterUrl("https://app.neosearch.neobim.ai/start")}
                className="flex items-center gap-2 text-orange-500 hover:text-orange-400 transition-all text-sm font-medium group-hover:gap-3"
                onClick={() => trackLead({ source: "livefeed" })}
              >
                {t.viewDetails}
                <span className="transition-transform group-hover:translate-x-1">
                  →
                </span>
              </a>
            </div>
          ))}
        </div>

        <div className="flex justify-center gap-2 mt-12">
          {Array.from({ length: Math.ceil(projects.length / 3) }).map(
            (_, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentIndex(idx * 3)}
                className={`w-2 h-2 rounded-full transition-all ${
                  Math.floor(currentIndex / 3) === idx
                    ? "bg-orange-500 w-8"
                    : "bg-slate-700 hover:bg-slate-600"
                }`}
                aria-label={`${t.goToSlideLabel} ${idx + 1}`}
              />
            )
          )}
        </div>
      </div>
    </section>
  );
}

export default LiveFeed;